/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Button,
    CountryPicker,
    EmailField,
    EmailValidation,
    FlexCol,
    FlexRow,
    formatMoney,
    Heading,
    Icon,
    RequiredValidation,
    StatePicker,
    TextField,
} from 'sarsaparilla';
import get from 'lodash/get';

import { isIssuanceFromAwardedLottery } from 'ui-permit/shared-components/utils/issuance-utils';
import {
    GROUP_DETAIL_ADDRESS1_PROP_PATH,
    GROUP_DETAIL_ADDRESS2_PROP_PATH,
    GROUP_DETAIL_CITY_PROP_PATH,
    GROUP_DETAIL_COUNTRY_PROP_PATH,
    GROUP_DETAIL_EMAIL_PROP_PATH,
    GROUP_DETAIL_FIRST_NAME_PROP_PATH,
    GROUP_DETAIL_LAST_NAME_PROP_PATH,
    GROUP_DETAIL_PHONE_PROP_PATH,
    GROUP_DETAIL_PROP_PATH,
    GROUP_DETAIL_STATE_PROP_PATH,
    GROUP_DETAIL_ZIPCODE_PROP_PATH,
} from 'ui-permit/shared-components/constants/propPaths';
import {
    CAROLINA_SANDHILLS_ID,
    LAS_VEGAS_NWR_DEER_ID,
    SOMERVILLE_LAKE_ID,
    MATTAMUSKEET_DISABLED_WATERFOWL,
    MATTAMUSKEET_YOUTH_WATERFOWL,
} from 'ui-permit/src/constants';

import DateSelector from '../DateSelector';
import {
    canSelectDate,
    getClassName,
    isDNRFieldRequired,
    isDNRLotteryRestricted,
    isHuntingLicenseNumberVisible,
} from '../../utils';
import { getHuntingDivisions } from '../../utils/getHuntingDivisions';

import PassType from '../PassType';
import DNRNumberInput from '../lottery-registration/DNRNumberInput';
import { permitTypes } from '../../constants/hunting-permit-constants';
import {
    GROUP_DETAIL_PASS_ID_PROP_PATH,
    GROUP_DETAIL_PASS_TYPE_PROP_PATH,
} from '../../constants';
import { readMaxGroupSizeRule } from '../../constants/registration';

const defaultDataBindingMap = {
    [GROUP_DETAIL_PROP_PATH]: 'permit_holder',
    [GROUP_DETAIL_FIRST_NAME_PROP_PATH]: 'first_name',
    [GROUP_DETAIL_LAST_NAME_PROP_PATH]: 'last_name',
};

const defaultAttributes = { requirePermissionCheck: true };

function isPriceBannerVisible(permitType) {
    return ![
        permitTypes.WATERFOWL_HUNT,
        permitTypes.ALLATOONA_HUNT,
        permitTypes.LAKE_GEORGETOWN_HUNT,
        permitTypes.GENERAL_FIREARMS,
        permitTypes.SPECIAL_ANTLERLESS,
        permitTypes.MUZZLE_LOADER,
        permitTypes.BELTON_LAKE_HUNT,
        permitTypes.LAKE_LANIER_HUNT,
        permitTypes.CAROLINA_SANDHILLS_HUNT,
        permitTypes.WALTER_F_GEORGE_LAKE,
        permitTypes.TENNESSEE_TOMBIGBEE_WATERWAY_HUNT,
        permitTypes.PIEDMONT_NWR_PRIMITIVE_HUNT,
        permitTypes.PIEDMONT_NWR_FIREARMS_HUNT_1,
        permitTypes.PIEDMONT_NWR_FIREARMS_HUNT_2,
        permitTypes.LAS_VEGAS_NWR_GOOSE_HUNT,
        permitTypes.PIEDMONT_NWR_TURKEY_HUNT_1,
        permitTypes.PIEDMONT_NWR_TURKEY_HUNT_2,
        permitTypes.PIEDMONT_NWR_TURKEY_HUNT_3,
        permitTypes.SOMERVILLE_LAKE_HUNT,
        permitTypes.HAVASU_WATERFOWL_HUNT,
        permitTypes.WHITNEY_AQUILLA_LAKES_HUNT,
        permitTypes.BOND_SWAMP_DEER_HUNT_1,
        permitTypes.BOND_SWAMP_DEER_HUNT_2,
        permitTypes.BOND_SWAMP_TURKEY_HUNT_1,
        permitTypes.BOND_SWAMP_TURKEY_HUNT_2,
        permitTypes.BOND_SWAMP_WATERFOWL_HUNT_1,
        permitTypes.BOND_SWAMP_WATERFOWL_HUNT_2,
        permitTypes.BOND_SWAMP_WATERFOWL_HUNT_3,
        permitTypes.BOND_SWAMP_WATERFOWL_HUNT_4,
        permitTypes.WACO_LAKE_HUNT,
        permitTypes.LAKE_GEORGETOWN_WATERFOWL_HUNT,
        permitTypes.LAKE_GEORGETOWN_OTHER_HUNT,
        permitTypes.OKEFENOKEE_SUWANNEE_CANAL_HUNT_1,
        permitTypes.OKEFENOKEE_SUWANNEE_CANAL_HUNT_2,
        permitTypes.MATTAMUSKEET_NWR_WATERFOWL_HUNT,
        permitTypes.MATTAMUSKEET_DISABLED_WATERFOWL_HUNT,
        permitTypes.MATTAMUSKEET_YOUTH_WATERFOWL_HUNT_1,
        permitTypes.MATTAMUSKEET_YOUTH_WATERFOWL_HUNT_2,
    ].includes(permitType);
}

export function isPassTypeVisible(permitType) {
    // TODO: this must be removed when migrated Hunting to Rules
    return ![
        permitTypes.WATERFOWL_HUNT,
        permitTypes.ALLATOONA_HUNT,
        permitTypes.LAKE_GEORGETOWN_HUNT,
        permitTypes.GENERAL_FIREARMS,
        permitTypes.SPECIAL_ANTLERLESS,
        permitTypes.MUZZLE_LOADER,
        permitTypes.BELTON_LAKE_HUNT,
        permitTypes.LAKE_LANIER_HUNT,
        permitTypes.CAROLINA_SANDHILLS_HUNT,
        permitTypes.WALTER_F_GEORGE_LAKE,
        permitTypes.TENNESSEE_TOMBIGBEE_WATERWAY_HUNT,
        permitTypes.PIEDMONT_NWR_PRIMITIVE_HUNT,
        permitTypes.PIEDMONT_NWR_FIREARMS_HUNT_1,
        permitTypes.PIEDMONT_NWR_FIREARMS_HUNT_2,
        permitTypes.LAS_VEGAS_NWR_GOOSE_HUNT,
        permitTypes.PIEDMONT_NWR_TURKEY_HUNT_1,
        permitTypes.PIEDMONT_NWR_TURKEY_HUNT_2,
        permitTypes.PIEDMONT_NWR_TURKEY_HUNT_3,
        permitTypes.SOMERVILLE_LAKE_HUNT,
        permitTypes.HAVASU_WATERFOWL_HUNT,
        permitTypes.WHITNEY_AQUILLA_LAKES_HUNT,
        permitTypes.BOND_SWAMP_DEER_HUNT_1,
        permitTypes.BOND_SWAMP_DEER_HUNT_2,
        permitTypes.BOND_SWAMP_TURKEY_HUNT_1,
        permitTypes.BOND_SWAMP_TURKEY_HUNT_2,
        permitTypes.BOND_SWAMP_WATERFOWL_HUNT_1,
        permitTypes.BOND_SWAMP_WATERFOWL_HUNT_2,
        permitTypes.BOND_SWAMP_WATERFOWL_HUNT_3,
        permitTypes.BOND_SWAMP_WATERFOWL_HUNT_4,
        permitTypes.WACO_LAKE_HUNT,
        permitTypes.LAKE_GEORGETOWN_WATERFOWL_HUNT,
        permitTypes.LAKE_GEORGETOWN_OTHER_HUNT,
        permitTypes.OKEFENOKEE_SUWANNEE_CANAL_HUNT_1,
        permitTypes.OKEFENOKEE_SUWANNEE_CANAL_HUNT_2,
        permitTypes.MATTAMUSKEET_NWR_WATERFOWL_HUNT,
        permitTypes.MATTAMUSKEET_DISABLED_WATERFOWL_HUNT,
        permitTypes.MATTAMUSKEET_YOUTH_WATERFOWL_HUNT_1,
        permitTypes.MATTAMUSKEET_YOUTH_WATERFOWL_HUNT_2,
    ].includes(permitType);
}

function isModifyGroupSizeVisible(permit, permitType, isReservationFromLottery) {
    const { id: permitID, rules } = permit;
    const maxGroupSize = readMaxGroupSizeRule(permitID, permitType, rules);

    // Disabled for permits in list
    const disabledFeaturePermits = [
        CAROLINA_SANDHILLS_ID,
        LAS_VEGAS_NWR_DEER_ID,
        SOMERVILLE_LAKE_ID,
        MATTAMUSKEET_YOUTH_WATERFOWL,
    ];

    return (
        !disabledFeaturePermits.includes(permitID) &&
        maxGroupSize > 1 &&
        !isReservationFromLottery
    );
}

export default (
    issuanceShape,
    dataBindingsMap = defaultDataBindingMap,
    attributes = defaultAttributes
) => {
    // create all prop mappings
    const dm = dataBindingsMap;
    const firstNamePropPath = (i) =>
        `${dm[GROUP_DETAIL_PROP_PATH]}[${i}].${dm[GROUP_DETAIL_FIRST_NAME_PROP_PATH]}`;
    const lastnamePropPath = (i) =>
        `${dm[GROUP_DETAIL_PROP_PATH]}[${i}].${dm[GROUP_DETAIL_LAST_NAME_PROP_PATH]}`;
    const emailPropPath = (i) =>
        `${dm[GROUP_DETAIL_PROP_PATH]}[${i}].${dm[GROUP_DETAIL_EMAIL_PROP_PATH]}`;
    const phonePropPath = (i) =>
        `${dm[GROUP_DETAIL_PROP_PATH]}[${i}].${dm[GROUP_DETAIL_PHONE_PROP_PATH]}`;

    const countryPropPath = (i) =>
        `${dm[GROUP_DETAIL_PROP_PATH]}[${i}].${dm[GROUP_DETAIL_COUNTRY_PROP_PATH]}`;
    const postalCodePropPath = (i) =>
        `${dm[GROUP_DETAIL_PROP_PATH]}[${i}].${dm[GROUP_DETAIL_ZIPCODE_PROP_PATH]}`;
    const address1PropPath = (i) =>
        `${dm[GROUP_DETAIL_PROP_PATH]}[${i}].${dm[GROUP_DETAIL_ADDRESS1_PROP_PATH]}`;
    const address2PropPath = (i) =>
        `${dm[GROUP_DETAIL_PROP_PATH]}[${i}].${dm[GROUP_DETAIL_ADDRESS2_PROP_PATH]}`;
    const statePropPath = (i) =>
        `${dm[GROUP_DETAIL_PROP_PATH]}[${i}].${dm[GROUP_DETAIL_STATE_PROP_PATH]}`;
    const cityPropPath = (i) =>
        `${dm[GROUP_DETAIL_PROP_PATH]}[${i}].${dm[GROUP_DETAIL_CITY_PROP_PATH]}`;

    const passTypePropPath = (i) =>
        `${dm[GROUP_DETAIL_PROP_PATH]}[${i}].${dm[GROUP_DETAIL_PASS_TYPE_PROP_PATH]}`;
    const passIdPropPath = (i) =>
        `${dm[GROUP_DETAIL_PROP_PATH]}[${i}].${dm[GROUP_DETAIL_PASS_ID_PROP_PATH]}`;

    class GroupDetail extends React.Component {
        onFormValidate(isInValid, error, path) {
            if (isInValid) {
                this.props.pActions.formValidationChange(
                    `${this.props.reservation.id}_${path}`,
                    error
                );
            } else {
                this.props.pActions.formValidationChange(
                    `${this.props.reservation.id}_${path}`,
                    null
                );
            }
        }

        handleValidation = (propPath) => (isInValid, error) =>
            this.onFormValidate(isInValid, error, propPath);

        handleUpdate = (propPath) => (e) =>
            this.props.pActions.updatePendingReservation(
                this.props.reservation.id,
                propPath,
                e.target.value
            );

        render() {
            const {
                dates,
                cartReservation,
                pActions,
                permit,
                permitTypeName,
                reservation,
                modifyLink,
                validation,
                displayValidationError,
                showGroupMembersList,
            } = this.props;
            const {
                id,
                permit_id: permitId,
                permit_type: huntType,
                division_id: divisionId,
            } = reservation;

            // Permit holder info
            const firstName = (i) => get(reservation, firstNamePropPath(i), '');
            const lastName = (i) => get(reservation, lastnamePropPath(i), '');
            const email = (i) => get(reservation, emailPropPath(i), '');
            const phone = (i) => get(reservation, phonePropPath(i), '');

            const firstNameValidation = (i) =>
                getClassName(
                    get(validation, `${id}_${firstNamePropPath(i)}`),
                    displayValidationError
                );
            const lastNameValidation = (i) =>
                getClassName(
                    get(validation, `${id}_${lastnamePropPath(i)}`),
                    displayValidationError
                );
            const emailValidation = (i) =>
                getClassName(
                    get(validation, `${id}_${emailPropPath(i)}`),
                    displayValidationError
                );
            const phoneValidation = (i) =>
                getClassName(
                    get(validation, `${id}_${phonePropPath(i)}`),
                    displayValidationError
                );

            // Address info
            const country = (i) => get(reservation, countryPropPath(i), '');
            const postalCode = (i) => get(reservation, postalCodePropPath(i), '');
            const address1 = (i) => get(reservation, address1PropPath(i), '');
            const address2 = (i) => get(reservation, address2PropPath(i), '');
            const state = (i) => get(reservation, statePropPath(i), '');
            const city = (i) => get(reservation, cityPropPath(i), '');
            const shouldUseStatePicker = (i) =>
                get(reservation, countryPropPath(i), '') === 'USA';

            const countryValidation = (i) =>
                getClassName(
                    get(validation, `${id}_${countryPropPath(i)}`),
                    displayValidationError
                );
            const postalCodeValidation = (i) =>
                getClassName(
                    get(validation, `${id}_${postalCodePropPath(i)}`),
                    displayValidationError
                );
            const address1Validation = (i) =>
                getClassName(
                    get(validation, `${id}_${address1PropPath(i)}`),
                    displayValidationError
                );
            const stateValidation = (i) =>
                getClassName(
                    get(validation, `${id}_${statePropPath(i)}`),
                    displayValidationError
                );
            const cityValidation = (i) =>
                getClassName(
                    get(validation, `${id}_${cityPropPath(i)}`),
                    displayValidationError
                );

            // Pass info
            const passType = (i) => get(reservation, passTypePropPath(i), '');
            const passId = (i) => get(reservation, passIdPropPath(i), '');

            const passIdValidation = (i) =>
                getClassName(
                    get(validation, `${id}_${passIdPropPath(i)}`),
                    displayValidationError
                );

            const shouldDisableGuestField = (memberNum) => {
                const isReservationOwner = memberNum === 0;
                let disableField;

                // to enable fields for future locations just add them to array
                if ([CAROLINA_SANDHILLS_ID, SOMERVILLE_LAKE_ID].includes(permitId)) {
                    disableField = isReservationOwner;
                } else {
                    disableField =
                        !isYouthHunt && (isReservationOwner || isReservationFromLottery);
                }

                return disableField;
            };

            // looks like if the permit has the word "disabled" in the name, then show pass fields, which we don't want to do for Mattamuskeet disabled waterfowl hunt (5393939)
            const ignoreDisabledHuntException = () =>
                permitId !== MATTAMUSKEET_DISABLED_WATERFOWL;

            const cantSelectDate = !canSelectDate(permit, huntType);
            const isYouthHunt = /youth\b.*\bhunt/.test(huntType);
            const isDisabledHunt =
                /disabled/.test(huntType) && ignoreDisabledHuntException();
            const huntingDivisions = getHuntingDivisions(permit, huntType, divisionId);
            const memberDescriptor = isYouthHunt ? 'Youth ' : '';
            const isReservationFromLottery = isIssuanceFromAwardedLottery(reservation);
            const displayAvailabilityRange =
                permit?.components?.display_availability_range;

            const showHuntingPassFields =
                isDisabledHunt ||
                isPassTypeVisible(huntType) || // TODO: this must be removed when migrated Hunting to Rules
                permit?.components?.hunting_pass;

            const showHuntingLicenseNumberField =
                isHuntingLicenseNumberVisible(permit.id) || // TODO: this must be removed when migrated Hunting to Rules
                permit?.components?.hunting_license_number;

            const isHuntingLicenseNumberDisabled =
                isReservationFromLottery &&
                isDNRLotteryRestricted(reservation?.permit_id, permit?.rules);

            let displayDate = dates.filter((d) =>
                cantSelectDate ? d : d === reservation.start_date
            );

            if (displayAvailabilityRange) {
                displayDate = [`${reservation.start_date}/${reservation.end_date}`];
            }

            let feePerPermit = 0;
            try {
                const useFee = cartReservation.cart_line_items.find(({ description }) =>
                    /Use Fee/.test(description)
                );
                const hasPass = /Pass/.test(useFee.description);
                feePerPermit = useFee.unit_price * (hasPass ? 2 : 1);
            } catch (e) {
                // Could not find Use Fee.
            }

            const pricingInfo = isDisabledHunt ? (
                <span>
                    Disabled hunting permits are <strong>$5.00</strong> per person.
                    <br />
                    Each group member MUST provide an access pass number to purchase a
                    disabled hunt permit.
                </span>
            ) : (
                <span>
                    {permitTypeName} hunting permits{' '}
                    {feePerPermit ? (
                        <span>
                            are <strong>{formatMoney(feePerPermit)}</strong> per person
                        </span>
                    ) : (
                        <span>only incur a reservation fee</span>
                    )}
                    .
                    <br />
                    {feePerPermit ? (
                        <span>
                            If you have a valid pass, you are entitled to a{' '}
                            <strong>50%</strong> discount.
                        </span>
                    ) : null}
                </span>
            );

            return (
                <div aria-live="polite">
                    {isPriceBannerVisible(huntType) && (
                        <Alert type="info" hasAriaLive>
                            <p>
                                <strong>Pricing</strong>
                                <br />
                                {pricingInfo}
                            </p>
                        </Alert>
                    )}
                    <FlexRow>
                        <FlexCol>
                            <div id="hunting-permit-form" className="hunting-permit-form">
                                <div className="hunting-permit-form-form">
                                    <FlexRow>
                                        <FlexCol>
                                            <p className="h6">Hunt</p>
                                        </FlexCol>
                                        <FlexCol>
                                            <p className="h6">Hunt Date</p>
                                        </FlexCol>
                                        {huntingDivisions !== 'Open Hunt' && (
                                            <FlexCol>
                                                <p className="h6">Hunt Area</p>
                                            </FlexCol>
                                        )}
                                    </FlexRow>
                                    <FlexRow>
                                        <FlexCol>{permitTypeName}</FlexCol>
                                        <FlexCol>
                                            <DateSelector
                                                isTextOnly
                                                isLabelVisible={false}
                                                dates={displayDate}
                                            />
                                        </FlexCol>
                                        {huntingDivisions !== 'Open Hunt' && (
                                            <FlexCol>{huntingDivisions}</FlexCol>
                                        )}
                                    </FlexRow>
                                    <FlexRow className="mt-8">
                                        <FlexCol>
                                            <p className="h5">Primary Permit Holder</p>
                                        </FlexCol>
                                        <FlexCol className="group-members-list-modify">
                                            {isModifyGroupSizeVisible(
                                                permit,
                                                huntType,
                                                isReservationFromLottery
                                            ) && (
                                                <Button
                                                    appearance="link"
                                                    iconBeforeElement={
                                                        <Icon iconName="edit" />
                                                    }
                                                    to={modifyLink}
                                                >
                                                    Modify{' '}
                                                    {cantSelectDate ? '' : 'date or '}
                                                    group size
                                                </Button>
                                            )}
                                        </FlexCol>
                                    </FlexRow>
                                    <FlexRow>
                                        <FlexCol>
                                            <div className="group-members-list">
                                                {reservation.group_members.map(
                                                    (member, memberNum) => (
                                                        <div key={memberNum}>
                                                            <FlexRow>
                                                                <FlexCol>
                                                                    {memberNum !== 0 &&
                                                                        showGroupMembersList && (
                                                                            <FlexRow>
                                                                                <FlexCol>
                                                                                    <p className="h5">
                                                                                        Group
                                                                                        Member{' '}
                                                                                        {memberNum +
                                                                                            1}
                                                                                    </p>
                                                                                </FlexCol>
                                                                            </FlexRow>
                                                                        )}
                                                                    {(memberNum === 0 ||
                                                                        showGroupMembersList) && (
                                                                        <FlexRow>
                                                                            <FlexCol>
                                                                                <div className="rec-form-item-wrap">
                                                                                    <label htmlFor="first-name">
                                                                                        {
                                                                                            memberDescriptor
                                                                                        }{' '}
                                                                                        First
                                                                                        Name
                                                                                        <abbr className="rec-label-required-indicator">
                                                                                            <span aria-hidden="true">
                                                                                                *
                                                                                            </span>
                                                                                            <span className="rec-sr-only">
                                                                                                {' '}
                                                                                                (Required){' '}
                                                                                            </span>
                                                                                        </abbr>
                                                                                    </label>
                                                                                    <input
                                                                                        id="first-name"
                                                                                        disabled={shouldDisableGuestField(
                                                                                            memberNum
                                                                                        )}
                                                                                        defaultValue={firstName(
                                                                                            memberNum
                                                                                        )}
                                                                                        className={firstNameValidation(
                                                                                            memberNum
                                                                                        )}
                                                                                        onChange={(
                                                                                            e
                                                                                        ) =>
                                                                                            pActions.updatePendingReservation(
                                                                                                id,
                                                                                                firstNamePropPath(
                                                                                                    memberNum
                                                                                                ),
                                                                                                e
                                                                                                    .target
                                                                                                    .value
                                                                                            )
                                                                                        }
                                                                                        type="text"
                                                                                    />
                                                                                    <RequiredValidation
                                                                                        value={firstName(
                                                                                            memberNum
                                                                                        )}
                                                                                        displayInSummary
                                                                                        onValidated={(
                                                                                            isInValid,
                                                                                            error
                                                                                        ) =>
                                                                                            this.onFormValidate(
                                                                                                isInValid,
                                                                                                error,
                                                                                                firstNamePropPath(
                                                                                                    memberNum
                                                                                                )
                                                                                            )
                                                                                        }
                                                                                        controlLabel="First Name"
                                                                                    />
                                                                                </div>
                                                                            </FlexCol>
                                                                            <FlexCol>
                                                                                <div className="rec-form-item-wrap">
                                                                                    <label htmlFor="last-name">
                                                                                        {
                                                                                            memberDescriptor
                                                                                        }{' '}
                                                                                        Last
                                                                                        Name
                                                                                        <abbr className="rec-label-required-indicator">
                                                                                            <span aria-hidden="true">
                                                                                                *
                                                                                            </span>
                                                                                            <span className="rec-sr-only">
                                                                                                {' '}
                                                                                                (Required){' '}
                                                                                            </span>
                                                                                        </abbr>
                                                                                    </label>
                                                                                    <input
                                                                                        id="last-name"
                                                                                        disabled={shouldDisableGuestField(
                                                                                            memberNum
                                                                                        )}
                                                                                        defaultValue={lastName(
                                                                                            memberNum
                                                                                        )}
                                                                                        className={lastNameValidation(
                                                                                            memberNum
                                                                                        )}
                                                                                        onChange={(
                                                                                            e
                                                                                        ) =>
                                                                                            pActions.updatePendingReservation(
                                                                                                id,
                                                                                                lastnamePropPath(
                                                                                                    memberNum
                                                                                                ),
                                                                                                e
                                                                                                    .target
                                                                                                    .value
                                                                                            )
                                                                                        }
                                                                                        type="text"
                                                                                    />
                                                                                    <RequiredValidation
                                                                                        value={lastName(
                                                                                            memberNum
                                                                                        )}
                                                                                        displayInSummary
                                                                                        controlLabel="Last Name"
                                                                                        onValidated={(
                                                                                            isInValid,
                                                                                            error
                                                                                        ) =>
                                                                                            this.onFormValidate(
                                                                                                isInValid,
                                                                                                error,
                                                                                                lastnamePropPath(
                                                                                                    memberNum
                                                                                                )
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            </FlexCol>
                                                                        </FlexRow>
                                                                    )}
                                                                    {memberNum === 0 && (
                                                                        <>
                                                                            <FlexRow>
                                                                                <FlexCol className="rec-form-item-wrap">
                                                                                    <EmailField
                                                                                        id="email"
                                                                                        label="Email"
                                                                                        value={email(
                                                                                            memberNum
                                                                                        )}
                                                                                        isDisabled
                                                                                        isRequired
                                                                                        isInvalid={
                                                                                            !!emailValidation(
                                                                                                memberNum
                                                                                            )
                                                                                        }
                                                                                        onChange={this.handleUpdate(
                                                                                            emailPropPath(
                                                                                                memberNum
                                                                                            )
                                                                                        )}
                                                                                    />
                                                                                    <RequiredValidation
                                                                                        controlLabel="Email"
                                                                                        value={email(
                                                                                            memberNum
                                                                                        )}
                                                                                        displayInSummary
                                                                                        onValidated={this.handleValidation(
                                                                                            emailPropPath(
                                                                                                memberNum
                                                                                            )
                                                                                        )}
                                                                                    />
                                                                                    <EmailValidation
                                                                                        controlLabel="Email"
                                                                                        value={email(
                                                                                            memberNum
                                                                                        )}
                                                                                        displayInSummary
                                                                                        onValidated={this.handleValidation(
                                                                                            emailPropPath(
                                                                                                memberNum
                                                                                            )
                                                                                        )}
                                                                                    />
                                                                                </FlexCol>
                                                                                <FlexCol className="rec-form-item-wrap">
                                                                                    <TextField
                                                                                        id="phone"
                                                                                        label="Phone Number"
                                                                                        value={phone(
                                                                                            memberNum
                                                                                        )}
                                                                                        isRequired
                                                                                        isInvalid={
                                                                                            !!phoneValidation(
                                                                                                memberNum
                                                                                            )
                                                                                        }
                                                                                        onChange={this.handleUpdate(
                                                                                            phonePropPath(
                                                                                                memberNum
                                                                                            )
                                                                                        )}
                                                                                    />
                                                                                    <RequiredValidation
                                                                                        controlLabel="Phone Number"
                                                                                        value={phone(
                                                                                            memberNum
                                                                                        )}
                                                                                        displayInSummary
                                                                                        onValidated={this.handleValidation(
                                                                                            phonePropPath(
                                                                                                memberNum
                                                                                            )
                                                                                        )}
                                                                                    />
                                                                                </FlexCol>
                                                                            </FlexRow>
                                                                            <Heading
                                                                                headingLevel={
                                                                                    2
                                                                                }
                                                                                appearance="h5"
                                                                            >
                                                                                Address
                                                                            </Heading>
                                                                            <FlexRow>
                                                                                <FlexCol
                                                                                    md={6}
                                                                                    className="rec-form-item-wrap"
                                                                                >
                                                                                    <CountryPicker
                                                                                        id="country"
                                                                                        label="Country"
                                                                                        value={country(
                                                                                            memberNum
                                                                                        )}
                                                                                        isRequired
                                                                                        isInvalid={
                                                                                            !!countryValidation(
                                                                                                countryPropPath(
                                                                                                    memberNum
                                                                                                )
                                                                                            )
                                                                                        }
                                                                                        onChange={this.handleUpdate(
                                                                                            countryPropPath(
                                                                                                memberNum
                                                                                            )
                                                                                        )}
                                                                                    />
                                                                                    <RequiredValidation
                                                                                        controlLabel="Country"
                                                                                        value={country(
                                                                                            memberNum
                                                                                        )}
                                                                                        displayInSummary
                                                                                        onValidated={this.handleValidation(
                                                                                            countryPropPath(
                                                                                                memberNum
                                                                                            )
                                                                                        )}
                                                                                    />
                                                                                </FlexCol>
                                                                            </FlexRow>
                                                                            <FlexRow>
                                                                                <FlexCol className="rec-form-item-wrap">
                                                                                    <TextField
                                                                                        id="address"
                                                                                        label="Street Address"
                                                                                        value={address1(
                                                                                            memberNum
                                                                                        )}
                                                                                        isRequired
                                                                                        isInvalid={
                                                                                            !!address1Validation(
                                                                                                memberNum
                                                                                            )
                                                                                        }
                                                                                        onChange={this.handleUpdate(
                                                                                            address1PropPath(
                                                                                                memberNum
                                                                                            )
                                                                                        )}
                                                                                    />
                                                                                    <RequiredValidation
                                                                                        controlLabel="Street Address"
                                                                                        value={address1(
                                                                                            memberNum
                                                                                        )}
                                                                                        displayInSummary
                                                                                        onValidated={this.handleValidation(
                                                                                            address1PropPath(
                                                                                                memberNum
                                                                                            )
                                                                                        )}
                                                                                    />
                                                                                </FlexCol>
                                                                                <FlexCol className="rec-form-item-wrap">
                                                                                    <TextField
                                                                                        id="suite-number"
                                                                                        label="Apt or Suite Number"
                                                                                        value={address2(
                                                                                            memberNum
                                                                                        )}
                                                                                        onChange={this.handleUpdate(
                                                                                            address2PropPath(
                                                                                                memberNum
                                                                                            )
                                                                                        )}
                                                                                    />
                                                                                </FlexCol>
                                                                            </FlexRow>
                                                                            <FlexRow>
                                                                                <FlexCol
                                                                                    md={6}
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                    className="rec-form-item-wrap"
                                                                                >
                                                                                    <TextField
                                                                                        id="city"
                                                                                        label="City / Town"
                                                                                        value={city(
                                                                                            memberNum
                                                                                        )}
                                                                                        isRequired
                                                                                        isInvalid={
                                                                                            !!cityValidation(
                                                                                                memberNum
                                                                                            )
                                                                                        }
                                                                                        onChange={this.handleUpdate(
                                                                                            cityPropPath(
                                                                                                memberNum
                                                                                            )
                                                                                        )}
                                                                                    />
                                                                                    <RequiredValidation
                                                                                        controlLabel="City / Town"
                                                                                        value={city(
                                                                                            memberNum
                                                                                        )}
                                                                                        displayInSummary
                                                                                        onValidated={this.handleValidation(
                                                                                            cityPropPath(
                                                                                                memberNum
                                                                                            )
                                                                                        )}
                                                                                    />
                                                                                </FlexCol>
                                                                                <FlexCol
                                                                                    md={3}
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                    className="rec-form-item-wrap"
                                                                                >
                                                                                    {shouldUseStatePicker(
                                                                                        memberNum
                                                                                    ) ? (
                                                                                        <>
                                                                                            <StatePicker
                                                                                                id="state"
                                                                                                label="State / Province"
                                                                                                value={state(
                                                                                                    memberNum
                                                                                                )}
                                                                                                isRequired
                                                                                                isInvalid={
                                                                                                    !!stateValidation(
                                                                                                        memberNum
                                                                                                    )
                                                                                                }
                                                                                                onChange={this.handleUpdate(
                                                                                                    statePropPath(
                                                                                                        memberNum
                                                                                                    )
                                                                                                )}
                                                                                            />
                                                                                            <RequiredValidation
                                                                                                controlLabel="State / Province"
                                                                                                value={state(
                                                                                                    memberNum
                                                                                                )}
                                                                                                displayInSummary
                                                                                                onValidated={this.handleValidation(
                                                                                                    statePropPath(
                                                                                                        memberNum
                                                                                                    )
                                                                                                )}
                                                                                            />
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            <TextField
                                                                                                id="state"
                                                                                                label="State / Province"
                                                                                                value={state(
                                                                                                    memberNum
                                                                                                )}
                                                                                                isRequired
                                                                                                isInvalid={
                                                                                                    !!stateValidation(
                                                                                                        memberNum
                                                                                                    )
                                                                                                }
                                                                                                onChange={this.handleUpdate(
                                                                                                    statePropPath(
                                                                                                        memberNum
                                                                                                    )
                                                                                                )}
                                                                                            />
                                                                                            <RequiredValidation
                                                                                                controlLabel="State / Province"
                                                                                                value={state(
                                                                                                    memberNum
                                                                                                )}
                                                                                                displayInSummary
                                                                                                onValidated={this.handleValidation(
                                                                                                    statePropPath(
                                                                                                        memberNum
                                                                                                    )
                                                                                                )}
                                                                                            />
                                                                                        </>
                                                                                    )}
                                                                                </FlexCol>
                                                                                <FlexCol
                                                                                    md={3}
                                                                                    className="rec-form-item-wrap"
                                                                                >
                                                                                    <TextField
                                                                                        id="postal-code"
                                                                                        label="Zip / Postal Code"
                                                                                        value={postalCode(
                                                                                            memberNum
                                                                                        )}
                                                                                        isRequired
                                                                                        isInvalid={
                                                                                            !!postalCodeValidation(
                                                                                                memberNum
                                                                                            )
                                                                                        }
                                                                                        onChange={this.handleUpdate(
                                                                                            postalCodePropPath(
                                                                                                memberNum
                                                                                            )
                                                                                        )}
                                                                                    />
                                                                                    <RequiredValidation
                                                                                        controlLabel="Zip / Postal Code"
                                                                                        value={postalCode(
                                                                                            memberNum
                                                                                        )}
                                                                                        displayInSummary
                                                                                        onValidated={this.handleValidation(
                                                                                            postalCodePropPath(
                                                                                                memberNum
                                                                                            )
                                                                                        )}
                                                                                    />
                                                                                </FlexCol>
                                                                            </FlexRow>
                                                                        </>
                                                                    )}
                                                                    {showHuntingPassFields && (
                                                                        <FlexRow>
                                                                            <FlexCol>
                                                                                <div className="rec-form-item-wrap">
                                                                                    <PassType
                                                                                        value={passType(
                                                                                            memberNum
                                                                                        )}
                                                                                        onChange={(
                                                                                            value
                                                                                        ) =>
                                                                                            pActions.updatePendingReservation(
                                                                                                id,
                                                                                                passTypePropPath(
                                                                                                    memberNum
                                                                                                ),
                                                                                                value
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            </FlexCol>
                                                                            <FlexCol>
                                                                                {passType(
                                                                                    memberNum
                                                                                ) && (
                                                                                    <div className="rec-form-item-wrap">
                                                                                        <label htmlFor="pass-id">
                                                                                            Pass
                                                                                            ID
                                                                                        </label>
                                                                                        <input
                                                                                            id="pass-id"
                                                                                            defaultValue={passId(
                                                                                                memberNum
                                                                                            )}
                                                                                            className={passIdValidation(
                                                                                                memberNum
                                                                                            )}
                                                                                            onChange={(
                                                                                                e
                                                                                            ) =>
                                                                                                pActions.updatePendingReservation(
                                                                                                    id,
                                                                                                    passIdPropPath(
                                                                                                        memberNum
                                                                                                    ),
                                                                                                    e
                                                                                                        .target
                                                                                                        .value
                                                                                                )
                                                                                            }
                                                                                            type="text"
                                                                                        />
                                                                                        <RequiredValidation
                                                                                            value={passId(
                                                                                                memberNum
                                                                                            )}
                                                                                            displayInSummary
                                                                                            controlLabel="Pass ID"
                                                                                            onValidated={(
                                                                                                isInValid,
                                                                                                error
                                                                                            ) =>
                                                                                                this.onFormValidate(
                                                                                                    isInValid,
                                                                                                    error,
                                                                                                    passIdPropPath(
                                                                                                        memberNum
                                                                                                    )
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </FlexCol>
                                                                        </FlexRow>
                                                                    )}
                                                                </FlexCol>
                                                            </FlexRow>
                                                            {showHuntingLicenseNumberField &&
                                                                memberNum === 0 && (
                                                                    <div>
                                                                        <FlexRow>
                                                                            <FlexCol>
                                                                                <div className="rec-form-item-wrap">
                                                                                    <DNRNumberInput
                                                                                        {...this
                                                                                            .props}
                                                                                        mdSize={
                                                                                            6
                                                                                        }
                                                                                        disabled={
                                                                                            isHuntingLicenseNumberDisabled
                                                                                        }
                                                                                        isRequired={isDNRFieldRequired(
                                                                                            reservation?.permit_id,
                                                                                            permit?.validations
                                                                                        )}
                                                                                    />
                                                                                </div>
                                                                            </FlexCol>
                                                                        </FlexRow>
                                                                    </div>
                                                                )}
                                                            {memberNum !==
                                                                reservation.group_members
                                                                    .length -
                                                                    1 &&
                                                                showGroupMembersList && (
                                                                    <hr />
                                                                )}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </FlexCol>
                                    </FlexRow>
                                </div>
                            </div>
                        </FlexCol>
                    </FlexRow>
                </div>
            );
        }
    }

    GroupDetail.propTypes = {
        validation: PropTypes.shape({}).isRequired,
        reservation: issuanceShape,
        pActions: PropTypes.shape({
            formValidationChange: PropTypes.func.isRequired,
        }).isRequired,
        displayValidationError: PropTypes.bool,
        hideGroupDetail: PropTypes.bool,
    };

    GroupDetail.defaultProps = {};

    GroupDetail.attributes = attributes;

    return GroupDetail;
};
